'use client';
import Script from 'next/script';
import { Script as ScriptType, ScriptsResponse } from '@/types/script/type';
import { fetchCustomTags } from '@/utils/custom-tag/api';
import { useEffect, useState } from 'react';
import { COUNTRY_LOCALE_MAP } from '@/lib/utils';
import { useDeferredScriptGate } from '@/hooks/useDeferredScriptGate';

interface ParsedScript {
  id: string;
  type: 'inline' | 'external' | 'html';
  content?: string;
  src?: string;
  attributes?: Record<string, string>;
  strategy: 'afterInteractive' | 'lazyOnload' | 'beforeInteractive';
  immediate?: boolean;
}

export default function BodyScripts({
  countryCode = 'au',
}: {
  countryCode: string;
}) {
  const locale = COUNTRY_LOCALE_MAP[countryCode];

  const [scripts, setScripts] = useState<ParsedScript[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const shouldLoadScripts = useDeferredScriptGate();

  useEffect(() => {
    const abortController = new AbortController();
    let active = true;

    const fetchScripts = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const data: ScriptsResponse = await fetchCustomTags(
          locale,
          abortController.signal
        );

        if (!active) return;

        if (!data) {
          throw new Error('Failed to fetch scripts data');
        }

        if (data.error) {
          throw new Error(data.error);
        }

        if (data.data) {
          const currentPath = window.location.pathname;
          const activeBodyScripts = data.data.body_scripts.filter((script) =>
            isScriptActive(script, currentPath)
          );

          const parsedScripts = activeBodyScripts.map(parseScript);
          setScripts(parsedScripts);

          parsedScripts.forEach((script) => {
            if (script.type === 'html' && script.content) {
              injectHtmlToBody(script.content, script.id);
            }
          });
        }
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') {
          return;
        }
        if (!active) return;
        const errorMessage =
          err instanceof Error ? err.message : 'An unknown error occurred';
        console.error('Error fetching body scripts:', errorMessage);
        setError(errorMessage);
      } finally {
        if (active) {
          setIsLoading(false);
        }
      }
    };

    fetchScripts();

    return () => {
      active = false;
      abortController.abort();
    };
  }, [locale]);

  const injectHtmlToBody = (html: string, id: string) => {
    if (typeof document === 'undefined') return;

    // Remove existing element with the same ID first (handles updates/locale switches)
    const existing = document.querySelectorAll(
      `[data-injected-body-id="${id}"]`
    );
    existing.forEach((el) => el.remove());

    // Create a temporary container to parse the HTML
    const temp = document.createElement('div');

    // Security: 'html' content is retrieved from a trusted CMS backend.
    // Direct innerHTML assignment is required to parse dynamic style/meta nodes.
    temp.innerHTML = html.trim();

    // Inject each element into the body
    Array.from(temp.children).forEach((element) => {
      const clonedElement = element.cloneNode(true) as Element;
      clonedElement.setAttribute('data-injected-body-id', id);
      document.body.appendChild(clonedElement);
    });
  };

  const isScriptActive = (script: ScriptType, currentPath: string): boolean => {
    if (!script?.injection_script_translations_id?.active) return false;
    if (script?.injection_script_translations_id?.apply_on_all_pages)
      return true;

    const filterValue = script?.injection_script_translations_id?.value || '';
    switch (script?.injection_script_translations_id?.filter) {
      case 'contains':
        return currentPath.includes(filterValue);
      case 'equal':
        return currentPath === filterValue;
      case 'starts_with':
        return currentPath.startsWith(filterValue);
      case 'ends_with':
        return currentPath.endsWith(filterValue);
      case null:
        return true;
      default:
        return false;
    }
  };

  const parseScript = (script: ScriptType): ParsedScript => {
    const scriptHtml = script?.injection_script_translations_id?.script.trim();

    // Check if it's an external script with src attribute
    const srcMatch = scriptHtml.match(
      /<script[^>]*src=["']([^"']+)["'][^>]*>/i
    );

    if (srcMatch) {
      // External script
      const src = srcMatch[1];
      const attributes: Record<string, string> = {};

      // Extract other attributes using traditional regex exec
      const attrRegex = /([\w-]+)(?:=["']([^"']*)["'])?/g;
      let attrMatch;
      while ((attrMatch = attrRegex.exec(scriptHtml)) !== null) {
        const [, attrName, attrValue] = attrMatch;
        if (
          attrName.toLowerCase() !== 'src' &&
          attrName.toLowerCase() !== 'script'
        ) {
          attributes[attrName] = attrValue !== undefined ? attrValue : 'true';
        }
      }

      const isImmediate =
        attributes['data-load'] === 'immediate' ||
        attributes['data-immediate'] === 'true' ||
        'data-load' in attributes ||
        'data-immediate' in attributes;

      // Body scripts are typically less critical, use lazyOnload by default
      let strategy: ParsedScript['strategy'] = 'lazyOnload';

      if (isImmediate) {
        strategy = 'afterInteractive';
      } else if (
        src.includes('gtag') ||
        src.includes('analytics') ||
        src.includes('gtm')
      ) {
        strategy = 'afterInteractive';
      } else if (
        src.includes('chat') ||
        src.includes('support') ||
        src.includes('widget')
      ) {
        strategy = 'lazyOnload';
      } else if (attributes.defer || attributes.async) {
        strategy = 'lazyOnload';
      }

      return {
        id: script?.injection_script_translations_id?.id.toString(),
        type: 'external',
        src,
        attributes,
        strategy,
        immediate: isImmediate,
      };
    } else if (scriptHtml.match(/<script[\s\S]*?>[\s\S]*?<\/script>/i)) {
      // Inline script
      const content = scriptHtml
        .replace(/<script\b[^>]*>([\s\S]*?)<\/script>/gi, '$1')
        .trim();

      const attributes: Record<string, string> = {};
      const openTagMatch = scriptHtml.match(/<script\b([^>]*)>/i);
      if (openTagMatch) {
        const openTagAttrs = openTagMatch[1];
        const attrRegex = /([\w-]+)(?:=["']([^"']*)["'])?/g;
        let attrMatch;
        while ((attrMatch = attrRegex.exec(openTagAttrs)) !== null) {
          const [, attrName, attrValue] = attrMatch;
          if (attrName.toLowerCase() !== 'script') {
            attributes[attrName] = attrValue !== undefined ? attrValue : 'true';
          }
        }
      }

      const isImmediate =
        attributes['data-load'] === 'immediate' ||
        attributes['data-immediate'] === 'true' ||
        'data-load' in attributes ||
        'data-immediate' in attributes;

      // Determine strategy based on content for body scripts
      let strategy: ParsedScript['strategy'] = 'lazyOnload';

      if (isImmediate) {
        strategy = 'afterInteractive';
      } else if (
        content.includes('gtag') ||
        content.includes('dataLayer') ||
        content.includes('GTM')
      ) {
        strategy = 'afterInteractive';
      } else if (
        content.includes('addEventListener') ||
        content.includes('DOMContentLoaded')
      ) {
        strategy = 'afterInteractive';
      }

      return {
        id: script?.injection_script_translations_id?.id.toString(),
        type: 'inline',
        content,
        attributes,
        strategy,
        immediate: isImmediate,
      };
    } else {
      // HTML elements (meta, link, style, etc)
      return {
        id: script?.injection_script_translations_id?.id.toString(),
        type: 'html',
        content: scriptHtml,
        strategy: 'afterInteractive',
      };
    }
  };

  if (isLoading) {
    return null;
  }

  if (error) {
    console.error('Body script injection error:', error);
    return null;
  }

  return (
    <>
      {scripts
        .filter((script) => script.type !== 'html')
        .filter((script) => script.immediate || shouldLoadScripts)
        .map((script) => {
          if (script.type === 'external') {
            return (
              <Script
                key={script.id}
                src={script.src}
                strategy={script.strategy}
                {...script.attributes}
                onLoad={() => {
                  console.log(`Body external script loaded: ${script.src}`);
                }}
                onError={(e) => {
                  console.error(`Failed to load body script: ${script.src}`, e);
                }}
              />
            );
          } else {
            return (
              <Script
                key={script.id}
                id={`body-inline-script-${script.id}`}
                strategy={script.strategy}
                dangerouslySetInnerHTML={{
                  __html: script.content || '',
                }}
                {...script.attributes}
                onLoad={() => {
                  console.log(`Body inline script loaded: ${script.id}`);
                }}
                onError={(e) => {
                  console.error(
                    `Failed to load body inline script: ${script.id}`,
                    e
                  );
                }}
              />
            );
          }
        })}
    </>
  );
}
